import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

export interface DocumentTab {
  uuid: string
  title: string
  projectKey?: string
  lastActiveAt: number
  /** 편집 중 미저장 변경 — TabBar 라벨 앞 • + 닫기 confirm */
  dirty?: boolean
}

interface DocumentTabsCtx {
  tabs: DocumentTab[]
  open: (tab: { uuid: string; title: string; projectKey?: string }) => void
  close: (uuid: string) => string | null
  /** 지정 uuid 외 모두 닫기. dirty 가 있는 tab 은 caller 가 confirm 후 force=true 로 호출. */
  closeOthers: (uuid: string) => void
  /** 지정 uuid 의 오른쪽에 있는 모든 tab 닫기. */
  closeToRight: (uuid: string) => void
  setDirty: (uuid: string, dirty: boolean) => void
}

const STORAGE_KEY = 'doc-tabs'
const Ctx = createContext<DocumentTabsCtx | null>(null)

function load(): DocumentTab[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x: unknown): x is DocumentTab =>
      typeof x === 'object' && x !== null
      && typeof (x as DocumentTab).uuid === 'string'
      && typeof (x as DocumentTab).title === 'string')
  } catch { return [] }
}

function save(tabs: DocumentTab[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs)) } catch {/* ignore quota */}
}

export function useDocumentTabs(): DocumentTabsCtx {
  const v = useContext(Ctx)
  if (!v) throw new Error('useDocumentTabs: DocumentTabsProvider 안에서만 사용')
  return v
}

export function DocumentTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<DocumentTab[]>(() => load())

  useEffect(() => { save(tabs) }, [tabs])

  const open = useCallback((tab: { uuid: string; title: string; projectKey?: string }) => {
    const now = Date.now()
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.uuid === tab.uuid)
      if (idx >= 0) {
        // 기존 — title/projectKey 갱신 + lastActiveAt
        const next = [...prev]
        next[idx] = { ...next[idx], title: tab.title, projectKey: tab.projectKey, lastActiveAt: now }
        return next
      }
      return [...prev, { ...tab, lastActiveAt: now }]
    })
  }, [])

  /** 닫고 다음 활성화할 인접 tab uuid 반환 (없으면 null). caller 가 navigate. */
  const close = useCallback((uuid: string): string | null => {
    let nextActive: string | null = null
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.uuid === uuid)
      if (idx < 0) return prev
      const next = prev.filter((_, i) => i !== idx)
      if (next.length > 0) {
        // 우측 우선, 없으면 좌측
        const neighbor = next[idx] ?? next[idx - 1] ?? next[next.length - 1]
        nextActive = neighbor?.uuid ?? null
      }
      return next
    })
    return nextActive
  }, [])

  const closeOthers = useCallback((keepUuid: string) => {
    setTabs((prev) => prev.filter((t) => t.uuid === keepUuid))
  }, [])

  const closeToRight = useCallback((pivotUuid: string) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.uuid === pivotUuid)
      if (idx < 0) return prev
      return prev.slice(0, idx + 1)
    })
  }, [])

  const setDirty = useCallback((uuid: string, dirty: boolean) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.uuid === uuid)
      if (idx < 0) return prev
      // 동일 값이면 no-op (불필요 re-render 방지)
      if (!!prev[idx].dirty === dirty) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], dirty }
      return next
    })
  }, [])

  return <Ctx.Provider value={{ tabs, open, close, closeOthers, closeToRight, setDirty }}>{children}</Ctx.Provider>
}
